/* global fetchTagCategoriesUrl storeTagCategoriesUrl fetchTagCategoryUrl storeTagsUrl updateTagsUrl importTagsUrl codesetSessionsUrl canManageTags */

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import classNames from 'classnames';
import { Modal } from 'flowbite';

import BudgetTagBar from './BudgetTagBar';
import { AVAILABILITY_EXCLUDED } from './budgetTags';
import CodesetSessionsList from './CodesetSessionsList';
import CreateTagCategoryModal from './CreateTagCategoryModal';
import CreateTagModal from './CreateTagModal';
import TagCategoryTab from './TagCategoryTab';

import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';

import FileIcon from 'Assets/icons/file.svg';
import PlusCircleIcon from 'Assets/icons/plus-circle.svg';

const CREATE_TAG_MODAL_ID = 'upsert-service-modal';

// Codeset tabs whose items can carry manual (custom) codes and therefore expose
// the contextual Add / Import actions. Custom codesets have type === null.
const WRITABLE_TYPES = [null, 'venues', 'client_groups'];
const SESSIONS_TYPE = 'sessions';

export default function TagsIndex(props) {
    const [tagCategories, setTagCategories] = useState([]);
    const [activeTagCategories, setActiveTagCategories] = useState([]);
    const [currentTagCategory, setCurrentTagCategory] = useState(null);
    const [currentTag, setCurrentTag] = useState(null);
    const [crateTagCategoryModalElement, setCrateTagCategoryModalElement] = useState(null);
    const [createtTagModalElement, setCreateTagModalElement] = useState(null);
    // Bumped to make the open tab refetch its items after a bulk taggable change.
    const [refreshKey, setRefreshKey] = useState(0);

    const crateTagCategoryModalRef = useRef();
    const createTagModalRef = useRef();

    useEffect(() => {
        fetchTagCategories();

        crateTagCategoryModalRef && setCrateTagCategoryModalElement(new Modal(crateTagCategoryModalRef.current, modalOptions(false)));
        createTagModalRef && setCreateTagModalElement(new Modal(createTagModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        if (currentTag) {
            showCreateTagModal();
        }
    }, [currentTag]);

    // Keeps the selected tab when it is still shown, otherwise selects the first.
    const fetchTagCategories = () => {
        axios.get(props.fetchUrl).then(({ data }) => {
            setTagCategories(data.tag_categories);
            setActiveTagCategories(data.active_tag_categories);
            setCurrentTagCategory((current) => data.active_tag_categories.find(({ id }) => id === current?.id) ?? data.active_tag_categories[0]);
        });
    };

    // Merges a codeset returned by a budget-tag/taggable update into every list.
    const updateCategory = (updated) => {
        const merge = (category) => (category?.id === updated.id ? { ...category, ...updated } : category);

        setTagCategories((categories) => categories.map(merge));
        setActiveTagCategories((categories) => categories.map(merge));
        setCurrentTagCategory(merge);
    };

    const onTagCreated = () => {
        setRefreshKey((key) => key + 1);
        fetchTagCategories();
    };

    const onTagEdit = (tag) => {
        setCurrentTag(tag);
    };

    const onAddTag = () => {
        setCurrentTag(null);
        showCreateTagModal();
    };

    const showCreateTagCategoryModal = () => {
        crateTagCategoryModalElement && crateTagCategoryModalElement.show();
    };

    const showCreateTagModal = () => {
        createtTagModalElement && createtTagModalElement.show();
    };

    const closeModal = () => {
        crateTagCategoryModalElement && crateTagCategoryModalElement.hide();
    };

    const isWritable = currentTagCategory && WRITABLE_TYPES.includes(currentTagCategory.type ?? null);
    const isSessions = currentTagCategory?.type === SESSIONS_TYPE;

    return (
        <div>
            <div className="tw-flex tw-justify-end tw-gap-x-2 tw-mb-6">
                {isWritable && !!props.canManageTags && (
                    <>
                        <Button as="a" variant="secondary" href={props.importTagsUrl.replace('TAG_CATEGORY_ID', currentTagCategory.id)} data-action="navigate">
                            <FileIcon className={'tw-w-3.5 tw-h-3.5 tw-me-2'} />
                            {$t('Import from file')}
                        </Button>
                        <Button variant="primary" onClick={onAddTag}>
                            <PlusCircleIcon className="tw-w-3.5 tw-h-3.5 tw-me-2" />
                            {$t('Add')}
                        </Button>
                    </>
                )}
                {!!props.canManageTags && (
                    <Button variant="primary" onClick={showCreateTagCategoryModal}>
                        {$t('Manage codesets')}
                    </Button>
                )}
            </div>
            <div className="tw-space-y-6">
                {!!activeTagCategories?.length && (
                    <>
                        <div className="in-nav horizontal" role="tablist">
                            {activeTagCategories?.map((category) => (
                                <button
                                    key={category.id}
                                    className={classNames('in-nav__item tw-gap-x-1.5', currentTagCategory?.id === category.id ? 'active' : 'inactive', {
                                        'tw-opacity-50': category.budget_tag_availability === AVAILABILITY_EXCLUDED,
                                    })}
                                    type="button"
                                    role="tab"
                                    onClick={() => setCurrentTagCategory(category)}
                                >
                                    {category.name}
                                    {category.is_budget_tag && (
                                        <span
                                            className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-green"
                                            title={$t('Budget tag')}
                                            aria-label={$t('Budget tag')}
                                        />
                                    )}
                                </button>
                            ))}
                        </div>
                        {currentTagCategory && (
                            <BudgetTagBar
                                key={currentTagCategory.id}
                                category={currentTagCategory}
                                canManageTags={!!props.canManageTags}
                                budgetTagUrl={props.budgetTagUrl}
                                taggableUrl={props.categoryTaggableUrl}
                                onUpdated={updateCategory}
                                onItemsChanged={() => setRefreshKey((key) => key + 1)}
                            />
                        )}
                        <div>
                            {isSessions ? (
                                <CodesetSessionsList fetchUrl={props.codesetSessionsUrl} />
                            ) : (
                                <TagCategoryTab
                                    tagCategoryId={currentTagCategory?.id}
                                    showUrl={props.showUrl}
                                    onTagEdit={onTagEdit}
                                    refreshKey={refreshKey}
                                    isBudgetTag={!!currentTagCategory?.is_budget_tag}
                                    canManageTags={!!props.canManageTags}
                                    onCategoryUpdated={updateCategory}
                                    onTagsChanged={fetchTagCategories}
                                />
                            )}
                        </div>
                    </>
                )}
            </div>

            <CreateTagModal
                modalId={CREATE_TAG_MODAL_ID}
                modalRef={createTagModalRef}
                modalElement={createtTagModalElement}
                tagCategory={currentTagCategory}
                tag={currentTag}
                storeUrl={props.storeTagsUrl}
                updateUrl={props.updateTagsUrl}
                onSuccess={onTagCreated}
            />

            <CreateTagCategoryModal
                canManageTags={!!props.canManageTags}
                modalId="create-tag-category-modal"
                modalRef={crateTagCategoryModalRef}
                modalElement={crateTagCategoryModalElement}
                storeUrl={props.storeUrl}
                tagCategories={tagCategories}
                activeTagCategories={activeTagCategories}
                onClose={closeModal}
                onCancel={closeModal}
                onSuccess={() => {
                    closeModal();
                    fetchTagCategories();
                }}
            />
        </div>
    );
}

if (document.getElementById('TagsIndex')) {
    var rootEl = document.getElementById('TagsIndex');
    var budgetTagUrl = rootEl.getAttribute('data-budget-tag-url');
    var categoryTaggableUrl = rootEl.getAttribute('data-category-taggable-url');
    ReactDOM.render(
        <App>
            <TagsIndex
                fetchUrl={fetchTagCategoriesUrl}
                storeUrl={storeTagCategoriesUrl}
                showUrl={fetchTagCategoryUrl}
                storeTagsUrl={storeTagsUrl}
                updateTagsUrl={updateTagsUrl}
                importTagsUrl={importTagsUrl}
                codesetSessionsUrl={codesetSessionsUrl}
                budgetTagUrl={budgetTagUrl}
                categoryTaggableUrl={categoryTaggableUrl}
                canManageTags={canManageTags}
            />
        </App>,
        rootEl,
    );
}
